class HeaderElement extends HTMLElement {
  #lastScrollPosition = 0;
  #headerHeight = 0;
  #headerTop = 0;
  #scrollFrame = 0;
  #measured = false;
  #offsetsWritten = false;
  #appliedScrolled = false;
  #appliedScrollUp = false;
  /** @type {HTMLElement | null} */
  #headerSection = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#headerSection = this.closest('.shopify-section--header');
    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(this);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    window.removeEventListener('scroll', this.#onScroll);
    if (this.#scrollFrame) {
      cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = 0;
    }
  }

  /**
   * Height comes from the observer entry, which is already measured.
   * Reading it here does not force layout.
   */
  #onResize = (entries) => {
    const nextHeight = entries[0]?.borderBoxSize?.[0]?.blockSize;
    if (typeof nextHeight !== 'number') return;
    if (this.#measured && Math.abs(nextHeight - this.#headerHeight) < 0.5) return;

    this.#headerHeight = nextHeight;

    if (!this.#measured) {
      this.#measured = true;
      // Read scroll position before publishing styles. A later read would force layout.
      const scrollY = window.scrollY;
      this.#lastScrollPosition = scrollY;
      this.#applyScroll(scrollY);
      return;
    }

    this.#writeOffsets();
  };

  #onScroll = () => {
    if (!this.#measured || this.#scrollFrame) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#applyScroll(window.scrollY);
    });
  };

  /**
   * @param {number} scrollY
   */
  #applyScroll(scrollY) {
    let headerTop = 0;
    let scrolled = false;
    let scrollUp = false;

    if (scrollY > this.#headerHeight) {
      const isScrollingDown = scrollY > this.#lastScrollPosition;
      headerTop = isScrollingDown ? this.#headerHeight : 0;
      scrolled = true;
      scrollUp = !isScrollingDown;
    }

    this.#lastScrollPosition = scrollY;

    const topChanged = headerTop !== this.#headerTop;
    if (topChanged) this.#headerTop = headerTop;
    if (topChanged || !this.#offsetsWritten) this.#writeOffsets();

    if (scrolled !== this.#appliedScrolled) {
      this.#appliedScrolled = scrolled;
      this.classList.toggle('scrolled', scrolled);
      this.#headerSection?.classList.toggle('scrolled', scrolled);
    }

    if (scrollUp !== this.#appliedScrollUp) {
      this.#appliedScrollUp = scrollUp;
      this.classList.toggle('scroll-up', scrollUp);
      this.#headerSection?.classList.toggle('scroll-up', scrollUp);
    }
  }

  #writeOffsets() {
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty('--header-height', `${this.#headerHeight}px`);
    rootStyle.setProperty('--header-top', `${this.#headerTop}px`);
    rootStyle.setProperty('--filter-bar-top', `${this.#headerHeight - this.#headerTop}px`);
    this.#offsetsWritten = true;
  }
}

customElements.define('header-element', HeaderElement);
